import {
  BackOfficePrivilegeName,
  OnlineTravelAgency,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { OutlinedSectionConfigProps } from '@new/domains/properties/_types'
import { LEGAL_DOCUMENTS_READ } from '@new/domains/properties/legalDocuments/legalDocumentsAccess'
import { PropertyCustomFieldSections } from '@new/domains/properties/screens/propertyInfo/PropertyInfo.types'
import { OnboardingSection } from '@new/domains/properties/screens/propertyOnboarding/Onboarding.types'
import { PropertyPolicyAndPermitsSections } from '@new/domains/properties/screens/propertyPolicyAndPermits/PropertyPolicyAndPermits.constants'
import { PropertyRevenueConfigurationSections } from '@new/domains/properties/screens/propertyRevenueConfiguration/PropertyRevenueConfiguration.types'

import { AuthStore } from '@contexts/Auth/AuthStore'
import { ROUTES } from '@routes'
import { formatOTAName } from '@utils/formatSectionName'

// All Sections
export const propertyInfoRoutes = [
  ROUTES.propertyInfo.path,
  ROUTES.propertyInternalConfiguration.path,
  ROUTES.propertyHomeownerInfo.path,
  ROUTES.propertyCommunications.path,
  ROUTES.propertyDistributionChannels.path,
  ROUTES.propertyRooms.path,
  ROUTES.propertyLayout.path,
  ROUTES.propertyRevenueConfiguration.path,
  ROUTES.propertyOnboarding.path,
  ROUTES.propertyPolicyAndPermits.path,
]

export const customFieldsSectionIdList = {
  [PropertyCustomFieldSections.generalConfiguration]: '0d730e32-86ff-11e9-b20d-0b15989c88b6',
  [PropertyCustomFieldSections.documents]: 'd6b38968-230b-11ea-a01a-503eaadea111',
  [PropertyCustomFieldSections.marketing]: '842915f4-14c5-11eb-8c33-dd673508989c',
  [PropertyCustomFieldSections.propertyMapping]: '341fe000-a93f-11eb-8136-673707c821db',
  [PropertyCustomFieldSections.leaseAndFinance]: 'ef596218-26f7-11ea-a680-503eaadea111',
  [PropertyCustomFieldSections.ownerAndLighthouse]: '6e58cc71-51f6-11eb-97e9-c792311be823',
  [PropertyCustomFieldSections.directAndOtherSettings]: 'a268c2bc-1c4a-11ea-b8f4-d787ffaf67ea',
  [PropertyCustomFieldSections.pricingTool]: 'daabf69a-9651-11ef-8416-b5fc054c1e90',
  [PropertyCustomFieldSections.paymentPlanSettings]: '3c30b390-3913-47fb-8dbc-77d3d891d2ad',
}

export const MAX_INTERNAL_WIDTH = 476

export const LEGAL_DOCUMENTS_SECTION_LABEL = 'Legal Documents'

export const propertyOutlinedSectionRoutes = [
  {
    label: 'Basic Info',
    path: ROUTES.propertyInfo.path,
  },
  {
    label: 'Cancellation Policies',
    path: ROUTES.propertyInfo.path,
  },
  {
    label: LEGAL_DOCUMENTS_SECTION_LABEL,
    path: ROUTES.propertyHomeownerInfo.path,
    visible: () => AuthStore.hasUserPermission(LEGAL_DOCUMENTS_READ),
  },
  {
    label: 'Lease Contracts',
    path: ROUTES.propertyHomeownerInfo.path,
  },
  {
    label: 'Owner Documents',
    path: ROUTES.propertyHomeownerInfo.path,
  },
  {
    label: 'Owner & Lighthouse',
    path: ROUTES.propertyHomeownerInfo.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.ownerAndLighthouse],
  },
  {
    label: 'Touchpoints',
    path: ROUTES.propertyHomeownerInfo.path,
    visible: () => AuthStore.hasUserPermission(BackOfficePrivilegeName.touchpointsRead),
  },
  {
    label: 'Welcome',
    path: ROUTES.propertyCommunications.path,
  },
  {
    label: 'Documents',
    path: ROUTES.propertyInternalConfiguration.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.documents],
  },
  {
    label: 'General Configuration',
    path: ROUTES.propertyInternalConfiguration.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.generalConfiguration],
  },
  {
    label: 'Lease & Finance',
    path: ROUTES.propertyInternalConfiguration.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.leaseAndFinance],
  },
  {
    label: 'Lifecycle',
    path: ROUTES.propertyInternalConfiguration.path,
  },
  {
    label: 'Pricing Tool',
    path: ROUTES.propertyInternalConfiguration.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.pricingTool],
  },
  {
    label: 'Property Mapping',
    path: ROUTES.propertyInternalConfiguration.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.propertyMapping],
  },
  {
    label: 'Cancellation Policies',
    path: ROUTES.propertyDistributionChannels.path,
    sectionId: 'CANCELLATION_POLICIES',
  },
  {
    label: 'Direct & Other Settings',
    path: ROUTES.propertyDistributionChannels.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.directAndOtherSettings],
  },
  {
    label: 'Highlights',
    path: ROUTES.propertyDistributionChannels.path,
    sectionId: 'highlights',
  },
  {
    label: 'Payment Plan Settings',
    path: ROUTES.propertyDistributionChannels.path,
    sectionId: customFieldsSectionIdList[PropertyCustomFieldSections.paymentPlanSettings],
  },
  {
    label: formatOTAName(OnlineTravelAgency.airbnb),
    path: ROUTES.propertyDistributionChannels.path,
    sectionId: OnlineTravelAgency.airbnb,
  },
  {
    label: formatOTAName(OnlineTravelAgency.rentalsUnited),
    path: ROUTES.propertyDistributionChannels.path,
    sectionId: OnlineTravelAgency.rentalsUnited,
  },
  {
    label: formatOTAName(OnlineTravelAgency.vrbo),
    path: ROUTES.propertyDistributionChannels.path,
    sectionId: OnlineTravelAgency.vrbo,
  },
  {
    label: 'Revenue Configuration',
    path: ROUTES.propertyRevenueConfiguration.path,
    sectionId: PropertyRevenueConfigurationSections.revenueConfiguration,
  },
  {
    label: 'Owner Restrictions',
    path: ROUTES.propertyRevenueConfiguration.path,
    sectionId: PropertyRevenueConfigurationSections.ownerRestrictions,
  },
  {
    label: 'OTA Promotions & Discounts',
    path: ROUTES.propertyRevenueConfiguration.path,
    sectionId: PropertyRevenueConfigurationSections.otaPromotionsAndDiscounts,
  },
  {
    label: 'Onboarding Info',
    path: ROUTES.propertyOnboarding.path,
    sectionId: OnboardingSection.onboardingInfo,
  },
  {
    label: 'Onboarding Cases',
    path: ROUTES.propertyOnboarding.path,
    sectionId: OnboardingSection.onboardingCases,
  },
  {
    label: 'Permitting',
    path: ROUTES.propertyPolicyAndPermits.path,
    sectionId: PropertyPolicyAndPermitsSections.permitting,
  },
  {
    label: 'Listings',
    path: ROUTES.propertyPolicyAndPermits.path,
    sectionId: PropertyPolicyAndPermitsSections.listings,
  },
  {
    label: 'Business License Information',
    path: ROUTES.propertyPolicyAndPermits.path,
    sectionId: PropertyPolicyAndPermitsSections.businessLicenseInformation,
  },
  {
    label: 'TOT',
    path: ROUTES.propertyPolicyAndPermits.path,
    sectionId: PropertyPolicyAndPermitsSections.tot,
  },
  {
    label: 'Permit Inspections',
    path: ROUTES.propertyPolicyAndPermits.path,
    sectionId: PropertyPolicyAndPermitsSections.permitInspections,
  },
] as OutlinedSectionConfigProps[]

// Homeowner Info
export const homeownerInfoCustomFieldsSections = [PropertyCustomFieldSections.ownerAndLighthouse]

// Internal Configuration
export const propertyInternalConfigurationCustomFieldsSections = [
  PropertyCustomFieldSections.generalConfiguration,
  PropertyCustomFieldSections.documents,
  PropertyCustomFieldSections.leaseAndFinance,
  PropertyCustomFieldSections.pricingTool,
  PropertyCustomFieldSections.propertyMapping,
]

// Internal Configuration
export const propertyListingAndDistributionCustomFieldsSections = [
  PropertyCustomFieldSections.directAndOtherSettings,
  PropertyCustomFieldSections.paymentPlanSettings,
]

export const ISO_DATE_FORMAT = 'yyyy-MM-dd'
